import { Alert, Button, Popconfirm, Space, Typography } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useAuthenticatedRequest } from '../../UserContext';
import { ConferenceApiError, ConferenceReport, conferenceRequest } from './api';

/** Manual mutations wait for Retry-After without being automatically replayed. */
export function useRetryWait(): [boolean, (delay: number) => void] {
  const [delay, setDelay] = useState(0);
  useEffect(() => {
    if (!delay) {
      return;
    }
    const timer = setTimeout(() => setDelay(0), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  return [delay > 0, setDelay];
}

/** Confirm soft deletion and retain the report whenever deletion is unconfirmed. */
export function DeleteReportAction({
  apiUrl,
  report,
  onDeleted,
  onUnavailable,
}: {
  apiUrl: string;
  report: ConferenceReport;
  onDeleted: () => void;
  onUnavailable: () => void;
}): JSX.Element {
  const authenticatedRequest = useAuthenticatedRequest();
  const [error, setError] = useState<string>();
  const [deleting, setDeleting] = useState(false);
  const [waiting, wait] = useRetryWait();
  const pending = useRef<AbortController>();
  useEffect(() => () => pending.current?.abort(), []);
  const label = ['preparing', 'queued', 'running'].includes(report.status)
    ? 'Stop and delete'
    : 'Delete report';

  const remove = async () => {
    if (pending.current || waiting) {
      return;
    }
    const controller = new AbortController();
    pending.current = controller;
    setDeleting(true);
    setError(undefined);
    try {
      await conferenceRequest(
        apiUrl,
        authenticatedRequest,
        'DELETE',
        controller.signal,
        report.report_id,
      );
      if (!controller.signal.aborted) {
        onDeleted();
      }
    } catch (error: unknown) {
      if (!controller.signal.aborted) {
        const failure =
          error instanceof ConferenceApiError
            ? error
            : new ConferenceApiError(0, 'DELETE');
        if (failure.status === 404) {
          onUnavailable();
        } else {
          setError(failure.message);
          if (failure.status === 409 || failure.retryAfterMs) {
            wait(failure.retryAfterMs ?? 10_000);
          }
        }
      }
    } finally {
      pending.current = undefined;
      if (!controller.signal.aborted) {
        setDeleting(false);
      }
    }
  };

  return (
    <Space direction="vertical">
      {error && <Alert message={error} showIcon type="error" />}
      <Popconfirm
        cancelText="Cancel"
        description={
          <div style={{ maxWidth: 360 }}>
            <Typography.Paragraph>
              Processing will stop and the FUB account will be released for
              resubmission. This is a soft deletion, not immediate removal of
              all stored artifacts.
            </Typography.Paragraph>
            <Typography.Paragraph>
              An email already submitted cannot be recalled. Work already in
              flight may finish before the worker observes deletion.
            </Typography.Paragraph>
          </div>
        }
        disabled={deleting || waiting}
        okButtonProps={{ danger: true }}
        okText={label}
        onConfirm={() => void remove()}
        title={
          label === 'Delete report'
            ? 'Delete this report?'
            : 'Stop and delete this report?'
        }
      >
        <Button
          danger
          disabled={waiting}
          loading={deleting}
          size="small"
          type="text"
        >
          {waiting ? 'Wait before retrying deletion' : label}
        </Button>
      </Popconfirm>
    </Space>
  );
}
